/**
 * Knowledge-check parsing and grading.
 *
 * `metadata` arrives as an open `Record<string, unknown>` (opaque to the
 * backend). These helpers tolerantly narrow it into a `KnowledgeCheckContent`
 * and grade learner answers without ever throwing — malformed or legacy data
 * degrades to sensible defaults rather than crashing the learner view.
 */

import {
  KNOWLEDGE_CHECK_QUESTION_TYPES,
  KnowledgeCheckContent,
  KnowledgeCheckOption,
  KnowledgeCheckQuestion,
  KnowledgeCheckQuestionType,
} from "@/modules/learningCenter/features/CourseManagement/types/course";

/** Outcome of grading a single question against a learner's selected option. */
export type KnowledgeCheckResult = "correct" | "incorrect" | "unanswered";

/** Map of question id → selected option id. */
export type KnowledgeCheckAnswers = Record<string, string>;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function parseOption(raw: unknown): KnowledgeCheckOption | null {
  if (!isRecord(raw)) return null;
  const { id, label, correct } = raw;
  if (typeof id !== "string" || typeof label !== "string") return null;
  const option: KnowledgeCheckOption = { id, label };
  if (correct === true) option.correct = true;
  return option;
}

function parseQuestionType(raw: unknown): KnowledgeCheckQuestionType {
  return typeof raw === "string" &&
    (KNOWLEDGE_CHECK_QUESTION_TYPES as readonly string[]).includes(raw)
    ? (raw as KnowledgeCheckQuestionType)
    : "single";
}

function parseQuestion(raw: unknown): KnowledgeCheckQuestion | null {
  if (!isRecord(raw)) return null;
  const { id, prompt, explanation } = raw;
  if (typeof id !== "string") return null;

  const options = Array.isArray(raw.options)
    ? raw.options
        .map(parseOption)
        .filter((o): o is KnowledgeCheckOption => o !== null)
    : [];

  const question: KnowledgeCheckQuestion = {
    id,
    type: parseQuestionType(raw.type),
    prompt: typeof prompt === "string" ? prompt : "",
    options,
  };
  if (typeof explanation === "string" && explanation.trim() !== "") {
    question.explanation = explanation;
  }
  return question;
}

/**
 * Tolerantly narrow an open metadata bag into a `KnowledgeCheckContent`.
 * Always returns `version: 2`; drops unparseable questions/options.
 */
export function parseKnowledgeCheck(
  metadata: Record<string, unknown> | null | undefined,
): KnowledgeCheckContent {
  const rawQuestions =
    metadata && Array.isArray(metadata.questions) ? metadata.questions : [];
  const questions = rawQuestions
    .map(parseQuestion)
    .filter((q): q is KnowledgeCheckQuestion => q !== null);

  const content: KnowledgeCheckContent = { version: 2, questions };
  const title = metadata?.title;
  if (typeof title === "string" && title.trim() !== "") {
    content.title = title;
  }
  return content;
}

/**
 * Grade a single question against a learner's selected option id.
 *
 * Supports `single` and `true_false` (both have exactly one correct option).
 * Returns `unanswered` when no option was selected, `incorrect` when the
 * question has no authored correct option (it can never be answered correctly).
 */
export function gradeQuestion(
  question: KnowledgeCheckQuestion,
  answer: string | null | undefined,
): KnowledgeCheckResult {
  if (answer === null || answer === undefined || answer === "") {
    return "unanswered";
  }
  const correctOption = question.options.find((o) => o.correct === true);
  if (!correctOption) return "incorrect";
  return answer === correctOption.id ? "correct" : "incorrect";
}

/** Tally correct answers across all questions. */
export function scoreKnowledgeCheck(
  questions: KnowledgeCheckQuestion[],
  answers: KnowledgeCheckAnswers,
): { correct: number; total: number } {
  let correct = 0;
  for (const question of questions) {
    if (gradeQuestion(question, answers[question.id]) === "correct") {
      correct += 1;
    }
  }
  return { correct, total: questions.length };
}
